import { BaseClient, normalizeQuery } from '../base-client';
import type { ExportCustomersQuery } from '../customers/types';
import type {
  CreateCustomerContactInput,
  CustomerContact,
  ImportContactsResult,
  PatchCustomerContactInput,
} from './types';

type One = { data: CustomerContact };
type Many = { data: CustomerContact[] };
type ImportResponse = { data: ImportContactsResult };

export class CustomerContactsClient extends BaseClient {
  /** Active contacts for a customer. Drives the customer-detail Contacts tab. */
  list(customerId: string): Promise<Many> {
    return this.request(`/api/customers/${encodeURIComponent(customerId)}/contacts`);
  }

  get(id: string): Promise<One> {
    return this.request(`/api/customer-contacts/${encodeURIComponent(id)}`);
  }

  create(customerId: string, input: CreateCustomerContactInput): Promise<One> {
    return this.request(`/api/customers/${encodeURIComponent(customerId)}/contacts`, {
      method: 'POST',
      body: input,
    });
  }

  patch(id: string, input: PatchCustomerContactInput): Promise<One> {
    return this.request(`/api/customer-contacts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  /** Soft delete — flips the contact's `row_status` to 'inactive'. */
  delete(id: string): Promise<One> {
    return this.request(`/api/customer-contacts/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  /**
   * Download the tenant's contacts as an Excel (.xlsx) blob. The owning
   * customer is identified by its Client ID so the file round-trips
   * against the customers export. `customerFilter` mirrors the customers
   * list filter (status / search / custom fields) so the export covers only
   * contacts of the customers visible on screen; pass `{}` to export all.
   */
  exportFile(
    customerFilter: ExportCustomersQuery = {},
  ): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/customer-contacts/export', {
      query: normalizeQuery(customerFilter),
    });
  }

  /** Download a blank Excel (.xlsx) template with only the import header row. */
  exportTemplate(): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/customer-contacts/import/template');
  }

  /**
   * Upload an .xlsx or .csv file. Best-effort upsert keyed on
   * (Client ID, first + last name): valid rows persist, invalid rows
   * come back with per-row errors. Pass `idempotencyKey` to make the
   * POST retry-safe.
   */
  importFile(file: File, opts: { idempotencyKey?: string } = {}): Promise<ImportResponse> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers: Record<string, string> = {};
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    return this.requestMultipart('/api/customer-contacts/import', fd, { headers });
  }
}
